import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Trash2, Edit, Eye, Copy, Download, MoreVertical, Search, Filter, ChevronDown, Inbox } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';

const FormBuilder = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [newFormDescription, setNewFormDescription] = useState('');
  const [editingForm, setEditingForm] = useState(null);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await api.get('/forms');
      setForms(response.data);
    } catch (error) {
      toast.error('Failed to fetch forms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateForm = async () => {
    if (!newFormName.trim()) {
      toast.error('Please enter a form name');
      return;
    }

    try {
      const response = await api.post('/forms', {
        name: newFormName,
        description: newFormDescription,
        fields: [],
        status: 'active'
      });
      setForms([response.data, ...forms]);
      setShowCreateModal(false);
      setNewFormName('');
      setNewFormDescription('');
      toast.success('Form created successfully');
      navigate(`/forms/${response.data.form_id}`);
    } catch (error) {
      toast.error('Failed to create form');
    }
  };

  const handleDuplicateForm = async (form) => {
    try {
      const response = await api.post('/forms', {
        name: `${form.name} (Copy)`,
        internal_name: form.internal_name ? `${form.internal_name} (Copy)` : undefined,
        description: form.description,
        fields: form.fields,
        success_message: form.success_message,
        submit_button_text: form.submit_button_text,
        status: 'active'
      });
      setForms([response.data, ...forms]);
      toast.success('Form duplicated successfully');
    } catch (error) {
      toast.error('Failed to duplicate form');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form and all its submissions?')) {
      return;
    }
    try {
      await api.delete(`/forms/${formId}`);
      setForms(forms.filter(f => f.form_id !== formId));
      toast.success('Form deleted successfully');
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const handleToggleStatus = async (form) => {
    const newStatus = form.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/forms/${form.form_id}`, { status: newStatus });
      setForms(forms.map(f => f.form_id === form.form_id ? { ...f, status: newStatus } : f));
      toast.success(`Form ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update form status');
    }
  };

  const filteredForms = forms.filter(form => {
    const q = search.toLowerCase();
    const matchesSearch = form.name.toLowerCase().includes(q) ||
      (form.internal_name && form.internal_name.toLowerCase().includes(q)) ||
      (form.description && form.description.toLowerCase().includes(q));
    const matchesStatus = statusFilter === 'all' || form.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Form Builder</h1>
            <p className="text-slate-500 mt-1">Create forms and capture submission data</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus size={18} className="mr-2" /> New Form
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search forms..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <Filter size={16} className="mr-2 text-slate-400" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Forms List */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading forms...</div>
        ) : filteredForms.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                <Inbox size={32} className="text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                {search || statusFilter !== 'all' ? 'No forms found' : 'No forms yet'}
              </h3>
              <p className="text-slate-500 mb-4 text-center max-w-md">
                {search || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Create your first form to start collecting data from your visitors'}
              </p>
              {!search && statusFilter === 'all' && (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus size={18} className="mr-2" /> Create Your First Form
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Form Name</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Fields</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Submissions</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Created</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredForms.map((form) => (
                  <tr key={form.form_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <FileText size={20} className="text-indigo-600" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900" data-testid={`form-list-name-${form.form_id}`}>
                            {form.internal_name || form.name}
                          </div>
                          {form.internal_name && (
                            <div className="text-xs text-slate-400">Public: {form.name}</div>
                          )}
                          {form.description && (
                            <div className="text-sm text-slate-500 truncate max-w-xs">{form.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {form.fields?.length || 0} fields
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/forms/${form.form_id}/submissions`)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        {form.submission_count || 0} submissions
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={form.status === 'active' ? 'default' : 'secondary'}>
                        {form.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {formatDate(form.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/forms/${form.form_id}`)}>
                            <Edit size={16} className="mr-2" /> Edit Form
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/forms/${form.form_id}/submissions`)}>
                            <Eye size={16} className="mr-2" /> View Submissions
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicateForm(form)}>
                            <Copy size={16} className="mr-2" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleToggleStatus(form)}>
                            {form.status === 'active' ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDeleteForm(form.form_id)}
                          >
                            <Trash2 size={16} className="mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Form Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Form</DialogTitle>
              <DialogDescription>Give your form a name and optional description</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="formName">Form Name *</Label>
                <Input
                  id="formName"
                  placeholder="Contact Form, Survey, Registration..."
                  value={newFormName}
                  onChange={(e) => setNewFormName(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="formDescription">Description (optional)</Label>
                <Textarea
                  id="formDescription"
                  placeholder="What is this form for?"
                  value={newFormDescription}
                  onChange={(e) => setNewFormDescription(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateForm} className="bg-indigo-600 hover:bg-indigo-700">
                Create Form
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default FormBuilder;
